/**
 * "Gift a stamp book" (toiyp Plus): give a correspondent a collection of stamp
 * designs. It is slow mail either way:
 *
 *  - with your next letter — it rides along and is theirs when that letter
 *    arrives; or
 *  - on its own, with a short note — a gift card posted now, arriving like a
 *    2nd-class letter. It is not a letter: it takes no turn and gives none.
 *
 * Or to someone not on toiyp yet (2026-10-20): the gift doubles as the
 * invitation. The giver writes a note and either gives an address (an invite
 * email that names the gift) or takes a link to send themselves. The book is
 * held until they join, and whoever arrives finds it in their stamp book and
 * a blank page addressed to the giver. Nobody comes within the week, and it
 * goes back to the giver's allowance.
 *
 * Opened from many quiet places (the stamp book, a book's own card, the
 * conversation menu and details, the composer's stamp shelf, the new-letter
 * panel) through `GiftBookLink`, which shows nothing at all to a member who
 * is not Plus.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Gift, Mail, Send, Share2, UserPlus, X } from 'lucide-react';
import { GIFT_NOTE_MAX, PLUS_LOCKED_NOTE } from '@toiyp/shared';
import {
  useGiftStampBookMutation,
  useGiftStampBookToNewcomerMutation,
  useMyStampsQuery,
  useWithdrawStampGiftMutation,
  type MyStamps,
  type StampCollection,
  type StampGift,
} from '../letters/api';
import { useMineQuery } from '../relationships/api';
import { useMeQuery } from '../profiles/api';
import { whatsAppShareUrl, withVia } from '../onboarding/share';
import { track } from '../onboarding/track';
import { giftShareText, heldFor, heldPhrase } from './newcomer';
import { Avatar } from '../profiles/avatar';
import { apiErrorMessage } from '../letters/ui';
import { arrivesPhrase } from '../letters/eta';
import { PostageStamp } from './Stamp';
import { postageNotice } from './notice';
import { bookContents } from './designs';
import { EnvelopeThumb } from '../stationery/Envelope';

const POST_OFFICE_ID = 'system-post-office';
/** The "who is it for?" choice for someone not on toiyp yet. */
const SOMEONE_NEW = 'someone-new';

function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'long' });
}

/**
 * A calm "Gift a stamp book" link that opens the gift sheet. Renders nothing
 * unless the member is Plus (no nag for anyone else), and nothing for the Post
 * Office's own pair.
 */
export function GiftBookLink({
  relationshipId,
  collectionId,
  label = 'Gift a stamp book',
  className = 'linkish gift-link',
  peerHandle,
  newcomer = false,
}: {
  relationshipId?: string;
  collectionId?: string;
  label?: string;
  className?: string;
  peerHandle?: string | null;
  /** Open straight on "someone new" (the new-letter panel). */
  newcomer?: boolean;
}) {
  const { data: stamps } = useMyStampsQuery();
  const [open, setOpen] = useState(false);
  if (!stamps?.plus || peerHandle === 'postmaster') return null;
  if (!stamps.collections.some((c) => c.plusOnly)) return null;
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        <Gift size={13} aria-hidden="true" />
        <span>{label}</span>
      </button>
      {open && (
        <GiftStampBook
          relationshipId={relationshipId ?? (newcomer ? SOMEONE_NEW : undefined)}
          collectionId={collectionId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function GiftStampBook({
  relationshipId: fixedId,
  collectionId: fixedCollection,
  onClose,
}: {
  /** The correspondent, when opened from their conversation. */
  relationshipId?: string;
  /** The book, when opened from its own card. */
  collectionId?: string;
  onClose: () => void;
}) {
  const { data: stamps, isLoading } = useMyStampsQuery();
  const { data: rels } = useMineQuery();
  const [gift, { isLoading: giving }] = useGiftStampBookMutation();
  const [withdraw] = useWithdrawStampGiftMutation();
  const [to, setTo] = useState<string | null>(fixedId ?? null);
  const [collectionId, setCollectionId] = useState<string | null>(fixedCollection ?? null);
  const [send, setSend] = useState<'letter' | 'card'>('letter');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const newcomer = to === SOMEONE_NEW;
  const people = useMemo(
    () => (rels ?? []).filter((r) => r.status === 'accepted' && r.peer && r.peer.userId !== POST_OFFICE_ID),
    [rels],
  );
  const peer = people.find((r) => r.id === to)?.peer ?? null;
  const name = peer?.handle ? `@${peer.handle}` : (peer?.displayName ?? 'them');
  const giftable: StampCollection[] = (stamps?.collections ?? []).filter((c) => c.plusOnly);
  const outgoing = (stamps?.outgoing ?? []).filter((g) =>
    !to ? true : newcomer ? g.kind === 'newcomer' : g.relationshipId === to,
  );
  // One gift card on the road to a person at a time.
  const cardOnTheRoad = outgoing.some((g) => g.kind === 'card' && g.state === 'travelling');

  const submit = async () => {
    if (!to || !collectionId) return;
    setError(null);
    try {
      const sent = await gift({
        relationshipId: to,
        collectionId,
        send,
        note: send === 'card' ? note.trim() : undefined,
      }).unwrap();
      postageNotice.show(
        sent.kind === 'card' && sent.deliverAt
          ? `Your stamp book is in the post to ${name}. It arrives ${arrivesPhrase(sent.deliverAt)}.`
          : `The stamp book will travel with your next letter to ${name}.`,
      );
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not wrap that one up. Try again in a moment.'));
    }
  };

  let body: React.ReactNode;
  if (isLoading || !stamps) {
    body = <p className="gift-book__note">Opening your stamp book…</p>;
  } else if (!stamps.plus) {
    body = (
      <p className="gift-book__note">
        Giving stamp books is {PLUS_LOCKED_NOTE.charAt(0).toLowerCase() + PLUS_LOCKED_NOTE.slice(1)}. A stamp book you’re
        given arrives in the post, like everything else here.
      </p>
    );
  } else if (!to) {
    body = (
      <>
        <p className="gift-book__lead">Who is it for?</p>
        <ul className="gift-book__people">
          <li>
            <button type="button" className="gift-book__person gift-book__person--new" onClick={() => setTo(SOMEONE_NEW)}>
              <span className="gift-book__avatar gift-book__avatar--new" aria-hidden="true">
                <UserPlus size={16} />
              </span>
              <span>
                Someone new
                <span className="gift-book__meta gift-book__meta--block">Not on toiyp yet. The stamp book is their welcome.</span>
              </span>
            </button>
          </li>
          {people.map((r) => (
            <li key={r.id}>
              <button type="button" className="gift-book__person" onClick={() => setTo(r.id)}>
                <span className="gift-book__avatar">
                  <Avatar name={r.peer!.displayName ?? r.peer!.handle ?? '?'} avatar={r.peer!.avatar ?? null} />
                </span>
                <span>{r.peer!.displayName ?? `@${r.peer!.handle}`}</span>
                {r.peer!.handle && <span className="gift-book__handle">@{r.peer!.handle}</span>}
              </button>
            </li>
          ))}

        </ul>
      </>
    );
  } else if (newcomer) {
    body = <ForSomeoneNew stamps={stamps} giftable={giftable} initialCollection={fixedCollection ?? null} onClose={onClose} />;
  } else {
    const waiting = outgoing.find((g) => g.state === 'waiting');
    body = (
      <>
        {stamps.gifting.left > 0 ? (
          <>
            <p className="gift-book__lead">Pick a stamp book for {name}</p>
            <BookChoices giftable={giftable} value={collectionId} onChange={setCollectionId} />

            <p className="gift-book__lead">How should it travel?</p>
            <div className="gift-book__ways" role="radiogroup" aria-label="How it travels">
              <button
                type="button"
                role="radio"
                aria-checked={send === 'letter'}
                className={`gift-book__way${send === 'letter' ? ' is-on' : ''}`}
                onClick={() => setSend('letter')}
              >
                <span className="gift-book__name">With my next letter</span>
                <span className="gift-book__meta">It’s theirs when that letter arrives.</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={send === 'card'}
                className={`gift-book__way${send === 'card' ? ' is-on' : ''}`}
                disabled={cardOnTheRoad}
                onClick={() => setSend('card')}
              >
                <span className="gift-book__name">On its own, with a note</span>
                <span className="gift-book__meta">
                  {cardOnTheRoad
                    ? 'Your last one to them is still on its way.'
                    : 'Posted now, it arrives like a 2nd-class letter. It isn’t a letter, so it doesn’t take your turn.'}
                </span>
              </button>
            </div>

            {send === 'card' && (
              <label className="gift-book__card">
                <span className="visually-hidden">A short note</span>
                <textarea
                  value={note}
                  maxLength={GIFT_NOTE_MAX}
                  rows={3}
                  placeholder={`A line for ${name} — what made you think of them?`}
                  onChange={(e) => setNote(e.target.value)}
                />
                <span className="gift-book__count">
                  {note.length}/{GIFT_NOTE_MAX}
                </span>
              </label>
            )}

            <p className="gift-book__note">
              {stamps.gifting.perMonth === 1 ? 'One stamp book a month.' : `${stamps.gifting.left} left this month.`}
            </p>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button
              type="button"
              className="btn btn--primary gift-book__cta"
              disabled={!collectionId || giving}
              onClick={() => void submit()}
            >
              {giving ? 'Wrapping…' : send === 'card' ? 'Post it' : 'Send with my next letter'}
            </button>
          </>
        ) : (
          <p className="gift-book__note">
            You’ve given this month’s stamp book. Another one is yours to give on {dayMonth(stamps.gifting.nextOn)}.
          </p>
        )}
        {outgoing.length > 0 && (
          <ul className="gift-book__outgoing" aria-label="On their way">
            {outgoing.map((g) => (
              <li key={g.id}>
                <span>
                  {g.collectionName} —{' '}
                  {g.kind === 'newcomer'
                    ? heldPhrase(g)
                    : g.kind === 'card' && g.deliverAt
                    ? `in the post, arrives ${arrivesPhrase(g.deliverAt)}`
                    : g.state === 'waiting'
                      ? 'waiting for your next letter'
                      : 'on its way with your letter'}
                </span>
                {g.state === 'waiting' && g === waiting && (
                  <button type="button" className="linkish" onClick={() => void withdraw({ id: g.id })}>
                    Keep it back
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }

  return createPortal(
    <div className="sheet-backdrop gift-book-backdrop" onClick={onClose}>
      <div
        className="sheet gift-book"
        role="dialog"
        aria-modal="true"
        aria-label="Gift a stamp book"
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__grip" aria-hidden="true" />
        <button type="button" className="stamp-picker__close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h2 className="gift-book__title">Gift a stamp book</h2>
        {body}
      </div>
    </div>,
    document.body,
  );
}

/** The stamp books a Plus member can give, as a single choice. */
function BookChoices({
  giftable,
  value,
  onChange,
}: {
  giftable: StampCollection[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <ul className="gift-book__books" role="radiogroup" aria-label="Stamp books">
      {giftable.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            role="radio"
            aria-checked={value === c.id}
            className={`gift-book__book${value === c.id ? ' is-on' : ''}`}
            onClick={() => onChange(c.id)}
          >
            <span className="gift-book__preview" aria-hidden="true">
              {c.designs.slice(0, c.papers.length ? 3 : 4).map((d) => (
                <PostageStamp key={d.id} cls="second" size="medium" design={d} />
              ))}
              {c.papers.slice(0, c.designs.length ? 1 : 3).map((p) => (
                <span key={p.id} className="gift-book__paper" style={{ backgroundImage: `url(${p.imageUrl})` }} />
              ))}
              {c.envelopes.slice(0, 1).map((e) => (
                <EnvelopeThumb key={e.id} envelope={e} />
              ))}
            </span>
            <span className="gift-book__name">{c.name}</span>
            <span className="gift-book__meta">
              {bookContents(c)}
              {c.description ? ` · ${c.description}` : ''}
            </span>
          </button>
        </li>
      ))}
      {giftable.length === 0 && <li className="gift-book__note">No stamp books to give just now.</li>}
    </ul>
  );
}

/**
 * A stamp book for someone not on toiyp yet. The note comes first in their
 * eyes, so it is asked for, not offered. Then an address (we send the
 * invitation) or a link (they send it, however they talk to them).
 */
function ForSomeoneNew({
  stamps,
  giftable,
  initialCollection,
  onClose,
}: {
  stamps: MyStamps;
  giftable: StampCollection[];
  initialCollection: string | null;
  onClose: () => void;
}) {
  const { data: me } = useMeQuery();
  const [give, { isLoading: giving }] = useGiftStampBookToNewcomerMutation();
  const [withdraw] = useWithdrawStampGiftMutation();
  const [collectionId, setCollectionId] = useState<string | null>(initialCollection);
  const [note, setNote] = useState('');
  const [reach, setReach] = useState<'link' | 'email'>('link');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [wrapped, setWrapped] = useState<StampGift | null>(null);
  const [shareMsg, setShareMsg] = useState<string | null>(null);
  const held = stamps.outgoing.filter((g) => g.kind === 'newcomer');

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = !!collectionId && note.trim().length > 0 && (reach === 'link' || emailOk);

  const submit = async () => {
    if (!ready || !collectionId) return;
    setError(null);
    try {
      const { gift, via } = await give({
        collectionId,
        note: note.trim(),
        email: reach === 'email' ? email.trim() : undefined,
      }).unwrap();
      if (via === 'link') {
        setWrapped(gift);
        return;
      }
      postageNotice.show(
        via === 'member'
          ? 'They’re already on toiyp, so your stamp book is in the post to them, with your note.'
          : `Your invitation is on its way to ${gift.invite?.email ?? 'them'}. The stamp book is theirs when they join.`,
      );
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not wrap that one up. Try again in a moment.'));
    }
  };

  if (wrapped?.invite?.url) {
    const url = wrapped.invite.url;
    const text = (via: 'wa' | 'sheet' | 'copy') =>
      giftShareText(me?.handle ?? 'me', wrapped.collectionName, withVia(url, via));
    const copy = async () => {
      track({ event: 'share_opened', channel: 'copy' });
      try {
        await navigator.clipboard.writeText(text('copy'));
        setShareMsg('Copied. Paste it wherever you talk to them.');
      } catch {
        setShareMsg('Copy the link above and send it to them.');
      }
    };
    const share = async () => {
      track({ event: 'share_opened', channel: 'sheet' });
      try {
        await navigator.share({ title: 'A stamp book for you', text: text('sheet') });
      } catch (e) {
        if ((e as { name?: string }).name !== 'AbortError') await copy();
      }
    };
    return (
      <div className="gift-book__wrapped">
        <p className="gift-book__lead">Your stamp book is wrapped</p>
        <p className="gift-book__note">
          Send this link to them. Whoever opens it first finds {wrapped.collectionName} in their stamp book, your note in
          their letters, and a blank page addressed to you.
        </p>
        <input
          className="field__input invite-link__url"
          value={url}
          readOnly
          aria-label="The stamp book's link"
          onFocus={(e) => e.currentTarget.select()}
        />
        <div className="gift-book__share">
          <a
            className="btn btn--primary gift-book__cta"
            href={whatsAppShareUrl(text('wa'))}
            target="_blank"
            rel="noreferrer"
            onClick={() => track({ event: 'share_opened', channel: 'whatsapp' })}
          >
            <Send size={15} aria-hidden="true" /> Send on WhatsApp
          </a>
          {typeof navigator.share === 'function' && (
            <button type="button" className="btn btn--ghost gift-book__cta" onClick={() => void share()}>
              <Share2 size={15} aria-hidden="true" /> Share…
            </button>
          )}
          <button type="button" className="btn btn--ghost gift-book__cta" onClick={() => void copy()}>
            <Copy size={15} aria-hidden="true" /> Copy link
          </button>
        </div>
        {shareMsg && (
          <p className="invite-friend__ok" role="status">
            {shareMsg}
          </p>
        )}
        <p className="gift-book__note">
          It stays open for one person until {wrapped.invite.expiresAt ? dayMonth(wrapped.invite.expiresAt) : 'next week'}.
          If nobody opens it by then, the stamp book comes back to you.
        </p>
        <button type="button" className="linkish" onClick={onClose}>
          Done
        </button>
      </div>
    );
  }

  return (
    <>
      {stamps.gifting.left > 0 ? (
        <>
          <p className="gift-book__lead">Pick a stamp book to welcome them</p>
          <BookChoices giftable={giftable} value={collectionId} onChange={setCollectionId} />

          <p className="gift-book__lead">Write them a note</p>
          <label className="gift-book__card">
            <span className="visually-hidden">A note to go with it</span>
            <textarea
              value={note}
              maxLength={GIFT_NOTE_MAX}
              rows={3}
              placeholder="Why this, and why them? It’s the first thing they’ll read from you."
              onChange={(e) => setNote(e.target.value)}
            />
            <span className="gift-book__count">
              {note.length}/{GIFT_NOTE_MAX}
            </span>
          </label>

          <p className="gift-book__lead">How should it reach them?</p>
          <div className="gift-book__ways" role="radiogroup" aria-label="How it reaches them">
            <button
              type="button"
              role="radio"
              aria-checked={reach === 'link'}
              className={`gift-book__way${reach === 'link' ? ' is-on' : ''}`}
              onClick={() => setReach('link')}
            >
              <span className="gift-book__name">
                <Send size={13} aria-hidden="true" /> A link you send them
              </span>
              <span className="gift-book__meta">On WhatsApp, in a message, however you talk to them.</span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={reach === 'email'}
              className={`gift-book__way${reach === 'email' ? ' is-on' : ''}`}
              onClick={() => setReach('email')}
            >
              <span className="gift-book__name">
                <Mail size={13} aria-hidden="true" /> By email
              </span>
              <span className="gift-book__meta">We’ll post them an invitation that says what’s waiting.</span>
            </button>
          </div>
          {reach === 'email' && (
            <label className="field">
              <span className="visually-hidden">Their email address</span>
              <input
                className="field__input"
                type="email"
                inputMode="email"
                autoComplete="off"
                placeholder="their@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
          )}

          <p className="gift-book__note">
            It counts as this month’s stamp book. It waits a week for them; if they haven’t come by then, it comes back
            to you.
          </p>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="button"
            className="btn btn--primary gift-book__cta"
            disabled={!ready || giving}
            onClick={() => void submit()}
          >
            {giving ? 'Wrapping…' : reach === 'email' ? 'Send the invitation' : 'Wrap it and make the link'}
          </button>
        </>
      ) : (
        <p className="gift-book__note">
          You’ve given this month’s stamp book. Another one is yours to give on {dayMonth(stamps.gifting.nextOn)}.
        </p>
      )}
      {held.length > 0 && (
        <ul className="gift-book__outgoing" aria-label="Waiting for someone new">
          {held.map((g) => (
            <li key={g.id}>
              <span>
                {g.collectionName} for {heldFor(g)} — {heldPhrase(g)}
              </span>
              <button type="button" className="linkish" onClick={() => void withdraw({ id: g.id })}>
                Keep it back
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
